import {
  BarChart as FacilityPerfIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  ShowChart as RoomDashboardIcon,
} from '@mui/icons-material'

import { urls as harvestUrls } from '~/src/Harvest/bundle'
import { getInventoryPermits } from '~/src/Inventory/utils'
import createRouteBundle from '~/src/Lib/createRouteBundle'
import { lazyWithRetries } from '~/src/Lib/Utils'
import { urls as recipeUrls } from '~/src/Recipe/bundle'
import { urls as roomUrls } from '~/src/Room/bundle'
import Forgot from '~/src/Routes/Forgot'
import Reset from '~/src/Routes/Forgot/reset'
import Login from '~/src/Routes/Login'
import RegisterGateway from '~/src/Routes/Register'
import AccountFlow from '~/src/Routes/Register/AccountFlow'
import AccountCreationError from '~/src/Routes/Register/AccountFlow/Steps/AccountCreationError'
import AccountCreationSuccess from '~/src/Routes/Register/AccountFlow/Steps/AccountCreationSuccess'
import GatewaySuccess from '~/src/Routes/Register/GatewaySuccess'
import Home from '~/src/Routes/SpinnerRoute'
import BatchIcon from '~/src/UI/Icons/Batch'
import JournalIcon from '~/src/UI/Icons/JournalNav'
import MetrcIcon from '~/src/UI/Icons/Metrc'
import PestIcon from '~/src/UI/Icons/Pest'
import Strain from '~/src/UI/Icons/Strain'
import StrainrunsIcon from '~/src/UI/Icons/Strainruns'
import { getRedirector } from '~/src/UI/Shared/RedirectTo'

import { PUBLIC_ROUTES, REGISTER_ROUTES } from './constants'
import Logout from './Logout'
import FlagAccountCreationWrapper from './Register/Flag/FlagWrapper'

const AccountProfile = lazyWithRetries(() => import('~/src/Account/Profile'))
const Account = lazyWithRetries(() => import('~/src/Account/Management'))
const ComplianceInbox = lazyWithRetries(() => import('~/src/Compliance/Routes'))
const Cultivars = lazyWithRetries(() => import('~/src/Cultivars'))
const CultivarProfile = lazyWithRetries(() => import('~/src/Cultivars/Profile'))
const CultivarSpecification = lazyWithRetries(() => import('~/src/Cultivars/Specification'))
const DevicesQR = lazyWithRetries(() => import('~/src/Device/qr'))
const FacilityDashboard = lazyWithRetries(() => import('~/src/Facility/Routes/Dashboard'))
const HarvestDetail = lazyWithRetries(() => import('~/src/Harvest/Detail'))
const HarvestCreate = lazyWithRetries(() => import('~/src/Harvest/Routes/Create'))
const IPMList = lazyWithRetries(() => import('~/src/IPM/Routes'))
const IPMPesticideList = lazyWithRetries(() => import('~/src/IPM/Routes/PesticideList'))
const AllEventsJournal = lazyWithRetries(() => import('~/src/Journal/Routes/AllEventsJournal'))
const FacilityPerformance = lazyWithRetries(() => import('~/src/Performance'))
const Production = lazyWithRetries(() => import('~/src/Production'))
const RecipeDetail = lazyWithRetries(() => import('~/src/Recipe/Detail'))
const RoomInventory = lazyWithRetries(() => import('~/src/Room/Inventory'))
const RoomDashboard = lazyWithRetries(() => import('~/src/Room/Routes/Dashboard'))
const RoomDashboardRedirect = lazyWithRetries(() => import('~/src/Room/Routes/Dashboard').then(
  ({ RedirectRoute }) => ({ default: RedirectRoute })
))
const NotFound = lazyWithRetries(() => import('~/src/Routes/NotFound'))
const Setup = lazyWithRetries(() => import('~/src/Setup'))
const StaffManagement = lazyWithRetries(() => import('~/src/Staff/Routes/Manage'))
const StrainrunAnalytics = lazyWithRetries(() => import('~/src/Strainrun/Analytics'))

export const roomRoute = '/rooms/:id'
export const inventoryRoute = '/inventory'

const ROOM_DASHBOARD_ROUTE_CONFIG = {
  component: RoomDashboard,
  actions: 'view_room',
}

const GROWLOG_ROUTE_CONFIG = {
  component: HarvestDetail,
  title: 'Harvest Growlog',
  actions: ['view_harvest'],
  negate: 'tiers',
  tiers: 'GO',
}

export default createRouteBundle({
  [PUBLIC_ROUTES.HOME]: {
    component: Home,
    publicAccess: true,
    viewMode: 'fullscreen',
  },
  '/dashboard': {
    component: FacilityDashboard,
    nav: {
      Icon: DashboardIcon,
      label: 'Facility Dashboard',
      sequence: 1,
    },
  },
  [PUBLIC_ROUTES.LOGIN]: {
    component: Login,
    publicAccess: true,
    title: 'Login',
    viewMode: 'fullscreen',
  },
  [PUBLIC_ROUTES.LOGOUT]: Logout,
  [PUBLIC_ROUTES.FORGOT]: {
    component: Forgot,
    publicAccess: true,
    title: 'Forgot Password?',
    viewMode: 'fullscreen',
  },
  [`${PUBLIC_ROUTES.FORGOT}/:email`]: {
    component: Forgot,
    publicAccess: true,
    title: 'Forgot Password?',
    viewMode: 'fullscreen',
  },
  [`${PUBLIC_ROUTES.RESET}/:token`]: {
    component: Reset,
    publicAccess: true,
    viewMode: 'fullscreen',
  },
  [PUBLIC_ROUTES.SWITCHING]: {
    additionalProps: {
      switching: true,
    },
    component: Login,
    publicAccess: true,
    title: 'Switching facilties',
    viewMode: 'fullscreen',
  },
  [`${REGISTER_ROUTES.GATEWAY}`]: {
    component: FlagAccountCreationWrapper,
    additionalProps: {
      handler: RegisterGateway
    },
    publicAccess: true,
    title: 'Register gateway',
    features: ['ACCOUNT_CREATION'],
  },
  [`${REGISTER_ROUTES.GATEWAY_SUCCESS}`]: {
    component: FlagAccountCreationWrapper,
    additionalProps: {
      handler: GatewaySuccess
    },
    publicAccess: true,
    title: 'Success',
    features: ['ACCOUNT_CREATION'],
  },
  [`${REGISTER_ROUTES.NEW_ACCOUNT}`]: {
    component: FlagAccountCreationWrapper,
    additionalProps: {
      handler: AccountFlow,
    },
    publicAccess: true,
    title: 'New Account',
    features: ['ACCOUNT_CREATION'],
  },
  [`${REGISTER_ROUTES.ACCOUNT_SUCCESS}`]: {
    component: FlagAccountCreationWrapper,
    additionalProps: {
      handler: AccountCreationSuccess,
    },
    publicAccess: true,
    title: 'Success',
    features: ['ACCOUNT_CREATION'],
  },
  [`${REGISTER_ROUTES.ACCOUNT_ERROR}`]: {
    component: FlagAccountCreationWrapper,
    additionalProps: {
      handler: AccountCreationError,
    },
    publicAccess: true,
    title: 'Success',
    features: ['ACCOUNT_CREATION'],
  },
  '/setup': {
    component: Setup,
    title: 'Setup',
    actions: ['change_facility', 'change_staff', 'view_device', 'change_compliance'],
    anyAction: true,
    nav: {
      Icon: SettingsIcon,
      sequence: 10,
    }
  },
  '/setup/inventory': {
    component: Setup,
    title: 'Setup - Inventory',
    actions: 'change_compliance',
  },
  '/setup/devices': {
    component: Setup,
    title: 'Setup - Devices',
    actions: 'view_device',
  },
  '/setup/controllers': {
    component: Setup,
    title: 'Setup - Irrigation Controllers',
    actions: 'view_irrigation',
  },
  '/setup/facility': {
    component: Setup,
    title: 'Setup - Facility',
    actions: 'change_facility',
  },
  '/setup/staff': {
    component: Setup,
    title: 'Setup - Staff',
    actions: 'change_staff',
  },
  '/rooms': {
    component: RoomDashboardRedirect,
    actions: 'view_room',
    nav: {
      Icon: RoomDashboardIcon,
      label: ({ outdoor }) => `${outdoor ? 'Field' : 'Room'} Dashboard`,
      sequence: 2,
      getURL: ({ rooms = [] }) => {
        if (rooms.length) {
          const [id] = rooms
          return roomUrls.getUrl({ id })
        }
        return roomUrls.list
      }
    }
  },
  '/rooms/:id/inventory': {
    component: RoomInventory,
    actions: ['view_room', getInventoryPermits],
  },
  '/rooms/:id': ROOM_DASHBOARD_ROUTE_CONFIG,
  '/rooms/:id/dashboard': ROOM_DASHBOARD_ROUTE_CONFIG,
  // '/r/:id/:slug': RoomDashboard,
  '/profile': {
    component: AccountProfile,
    title: 'Your Profile',
  },
  '/account': {
    component: Account,
    title: 'Your Account',
    // actions: 'manage_account',
    features: 'CRM_INTEGRATION',
    Fallback: AccountProfile,
    PermissionFallback: NotFound
  },
  '/journal': {
    component: AllEventsJournal,
    nav: {
      label: 'Journal',
      Icon: JournalIcon,
      sequence: 4,
    }
  },
  '/qr/:sn': {
    component: DevicesQR,
  },
  '/staff/manage': {
    component: StaffManagement,
    title: 'Manage Staff',
    actions: 'change_staff',
    negate: 'tiers',
    tiers: 'GO',
  },
  '/staff/:id': {
    component: AccountProfile,
    actions: 'change_staff',
  },
  '/ipm/pesticides': {
    component: IPMPesticideList,
    title: 'IPM Pesticides',
    actions: 'change_pesticide_application',
    negate: 'tiers',
    tiers: 'GO',
  },
  '/ipm': {
    component: IPMList,
    title: 'IPM Events',
    actions: 'view_pesticide_application',
    negate: 'tiers',
    tiers: 'GO',
    nav: {
      Icon: PestIcon,
      sequence: 5,
    }
  },
  '/production': {
    component: Production,
    title: 'Production',
    actions: ['view_harvest', 'view_recipe', 'view_package', 'view_plant'],
    anyAction: true,
    negate: 'tiers',
    tiers: 'GO',
    nav: {
      Icon: BatchIcon,
      sequence: 6,
      urlList: ['/harvests', '/recipes', '/inventory'],
      negate: 'tiers',
      tiers: 'GO',
    }
  },
  '/irrigationSchedules': getRedirector('/irrigationTemplates'),
  '/irrigationTemplates': {
    component: Production,
    title: 'Irrigation schedules',
    actions: ['view_irrigation'],
  },
  [inventoryRoute]: {
    component: Production,
    title: 'Inventory',
    actions: [getInventoryPermits],
    features: ['METRC'],
  },
  [recipeUrls.list]: {
    component: Production,
    title: 'Recipes',
    actions: 'view_recipe'
  },
  [recipeUrls.create]: {
    component: RecipeDetail,
    title: 'Create Recipe',
    actions: 'change_recipe',
  },
  [recipeUrls.view]: {
    component: RecipeDetail,
    actions: 'view_recipe',
  },
  [harvestUrls.list]: {
    component: Production,
    title: 'Harvests',
    actions: 'view_harvest',
    negate: 'tiers',
    tiers: 'GO'
  },
  [harvestUrls.cultivars]: {
    component: HarvestDetail,
    title: 'Cultivars',
    permissions: 'view_harvest',
  },
  [harvestUrls.create]: {
    component: HarvestCreate,
    title: 'Create Harvest',
    actions: 'change_harvest',
    negate: 'tiers',
    tiers: 'GO'
  },
  [harvestUrls.view]: GROWLOG_ROUTE_CONFIG,
  [harvestUrls.schedule]: {
    component: HarvestDetail,
    title: 'Harvest Schedule',
    actions: 'view_recipe',
    negate: 'tiers',
    tiers: 'GO',
  },
  [harvestUrls.yield]: {
    component: HarvestDetail,
    title: 'Harvest Yield',
    actions: 'view_harvest',
    features: ['METRC'],
    negate: ['features', 'tiers'],
    tiers: 'GO',
  },
  [harvestUrls.growlog]: GROWLOG_ROUTE_CONFIG,
  [harvestUrls.analytics]: {
    component: HarvestDetail,
    title: 'Harvest Analytics',
    actions: 'view_harvest',
    negate: 'tiers',
    tiers: 'GO',
  },
  [harvestUrls.inventory]: {
    component: HarvestDetail,
    title: 'Harvest Inventory',
    actions: ['view_harvest', getInventoryPermits],
    negate: 'tiers',
    tiers: 'GO',
  },
  '/runs': {
    title: 'Runs',
    component: StrainrunAnalytics,
    features: 'STRAINRUN_ANALYTICS',
    negate: 'tiers',
    tiers: 'GO',
    nav: {
      Icon: StrainrunsIcon,
      sequence: 7,
    }
  },
  '/facility/performance': {
    component: FacilityPerformance,
    title: 'Facility Performance',
    actions: 'view_performance',
    features: 'FACILITY_PERFORMANCE',
    negate: 'tiers',
    tiers: 'GO',
    nav: {
      Icon: FacilityPerfIcon,
      sequence: 9,
    }
  },
  '/cultivars': {
    component: Cultivars,
    features: ['CULTIVAR_PROFILES', 'CULTIVAR_REPORT_CARD'],
    anyFeature: true,
    actions: 'view_cultivar_profiles',
    title: 'Cultivars',
    negate: 'tiers',
    tiers: 'GO',
    nav: {
      Icon: Strain,
      sequence: 8,
    }
  },
  '/cultivars/:id': {
    component: CultivarSpecification,
    features: ['CULTIVAR_PROFILES', 'CULTIVAR_REPORT_CARD'],
    anyFeature: true,
    actions: 'view_cultivar_profiles',
    negate: 'tiers',
    tiers: 'GO',
    title: 'Cultivar Profile',
    Fallback: CultivarProfile,
    PermissionFallback: NotFound,
  },
  '/compliance': {
    component: ComplianceInbox,
    actions: ['sync_cultivation', 'sync_processing', 'view_inbox'],
    additionalProps: { inbox: true },
    anyAction: true,
    features: ['METRC'],
    nav: {
      label: 'Compliance Inbox',
      Icon: MetrcIcon,
      sequence: 3,
    }
  },
  '/compliance/history': {
    component: ComplianceInbox,
    actions: ['sync_cultivation', 'sync_processing', 'view_inbox'],
    additionalProps: { history: true },
    anyAction: true,
    features: ['METRC'],
  },
  '/compliance/tags': {
    component: ComplianceInbox,
    actions: ['sync_cultivation', 'sync_processing', 'view_inbox'],
    additionalProps: { tags: true },
    anyAction: true,
    features: ['METRC'],
  },
  [PUBLIC_ROUTES.NOT_FOUND]: {
    component: NotFound,
    publicAccess: true,
    is404: true,
    title: 'Unable to locate resource',
  },
})
